import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { task } from 'ember-concurrency'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type Student from 'teacher-ui-client/models/student'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import { optional } from 'teacher-ui-client/helpers/optional'
import UiButton from 'teacher-ui-client/components/ui-button'
import UiButtonState from 'teacher-ui-client/components/ui-button/state'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type { Log } from '@blakeelearning/log'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'

interface Signature {
  Element: HTMLDivElement
  Args: {
    students: Student[]
    themeKey?: ThemeKeys
    closeModalAction?: () => unknown
    onSuccess?: () => unknown
  }
}

interface StudentRow {
  id: string
  fullName: string
  schoolClassesData: {
    label: string
    classNameAndTeachers: string
  }[]
}

interface Theme {
  alertHeaderBg: string
  headerBg: string
  rowBg: string
}

const themeClasses: Partial<Record<ThemeKeys, Theme>> = {
  [ThemeKeys.blue]: {
    alertHeaderBg: 'bg-oceany-blue-300',
    headerBg: 'bg-oceany-blue-100',
    rowBg: 'even:bg-oceany-blue-50 hover:bg-oceany-blue-100',
  },
  [ThemeKeys.green]: {
    alertHeaderBg: 'bg-ms-green-300',
    headerBg: 'bg-ms-green-100',
    rowBg: 'even:bg-ms-green-50 hover:bg-ms-green-100',
  },
  [ThemeKeys.darkBlue]: {
    alertHeaderBg: 'bg-wl-blue-250',
    headerBg: 'bg-wl-blue-100',
    rowBg: 'even:bg-wl-blue-50 hover:bg-wl-blue-100',
  },
  [ThemeKeys.default]: {
    alertHeaderBg: 'bg-dusty-black-300',
    headerBg: 'bg-dusty-black-100',
    rowBg: 'even:bg-dusty-black-50 hover:bg-dusty-black-100',
  },
}

export class FormDeleteStudents extends Component<Signature> {
  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @service subscriptionType!: SubscriptionTypeService

  @service resourceConflict!: ResourceConflictService

  @service log!: Log

  @tracked studentRows: StudentRow[] = []

  schoolClassesViewModel(labelKey: string, schoolClasses: SchoolClass[]) {
    return {
      label: this.intl.t(`manage.manageClasses.students.bulkActions.deleteStudents.subscriptionType.${labelKey}`),
      // Class A (Teacher 1, Teacher 2), Class B (Teacher 3)
      classNameAndTeachers: schoolClasses.map((sc) => `${sc.name} (${sc.teacherNames.join(', ')})`).join(', '),
    }
  }

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    // Set studentRows here, so that what is rendered persists during and after deletion, until the form is closed
    this.studentRows = this.args.students.filter(hasId).map((student) => {
      return {
        id: student.id,
        fullName: student.fullName,
        schoolClassesData: [
          this.schoolClassesViewModel('reading', student.readingSchoolClasses),
          this.schoolClassesViewModel('maths', student.mathsSchoolClasses),
          this.schoolClassesViewModel('writing', student.writingSchoolClasses),
          this.schoolClassesViewModel('wf', student.wordFlyersSchoolClasses),
        ],
      }
    })
  }

  get themeClasses() {
    return themeClasses[this.args.themeKey ?? ThemeKeys.default]
  }

  get hasStudentsInOtherClasses() {
    return (
      this.args.students.filter((student) =>
        student.schoolClasses.some((schoolClass) => schoolClass.subscriptionType !== this.subscriptionType.current),
      ).length > 0
    )
  }

  get deletableStudentCount() {
    return this.studentRows.length
  }

  get isDeleting() {
    return this.deleteStudents.isRunning
  }

  @action
  onSubmit(event: Event) {
    event.preventDefault()

    void this.deleteStudents.perform(this.args.students)
  }

  deleteStudents = task({ drop: true }, async (students: Student[]) => {
    const deletedCount = students.length
    try {
      // the students' bulk manager will handle this in bulk, and batch those requests
      const destroyOptions = {
        adapterOptions: {
          bulk: true,
          additionalBulkData: { product: this.subscriptionType.current, delete_student_classes: true },
        },
      }
      const promises = students.map((student) => student.destroyRecord(destroyOptions))
      await Promise.all(promises)

      this.args.closeModalAction?.()

      this.flashQueue.addSuccess({
        subtitle: this.intl.t('manage.manageClasses.students.bulkActions.deleteStudents.success', {
          count: deletedCount,
        }),
      })

      this.args.onSuccess?.()
    } catch (error) {
      if (isConflictError(error)) {
        this.resourceConflict.handleError('delete students', error)
      } else {
        this.log.error(
          'failed to delete students',
          students.map((s) => s.id),
          error ?? '',
        )
        this.flashQueue.addFail({
          subtitle: this.intl.t('manage.manageClasses.students.bulkActions.deleteStudents.fail'),
        })
      }
    }
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <div ...attributes>
      {{#if this.hasStudentsInOtherClasses}}
        <div class="px-4 pb-4 {{this.themeClasses.alertHeaderBg}}" data-test-other-classes-warning>
          <InlineAlert @type={{AlertType.Warning}} role="alert">
            <InlineAlertSubtitle>
              {{t "manage.manageClasses.students.bulkActions.deleteStudents.otherClassesWarning"}}
            </InlineAlertSubtitle>
          </InlineAlert>
        </div>
      {{/if}}

      <div class="px-4 pb-4 {{this.themeClasses.alertHeaderBg}}">
        <InlineAlert @type={{AlertType.Critical}} role="alert">
          <InlineAlertSubtitle>

            {{t
              "manage.manageClasses.students.bulkActions.deleteStudents.confirm"
              count=this.deletableStudentCount
              htmlSafe=true
            }}
          </InlineAlertSubtitle>

        </InlineAlert>
      </div>

      <form {{on "submit" this.onSubmit}}>
        <div class="max-h-80 overflow-y-auto">
          <table class="w-full" tabindex="0">
            <thead>
              <tr class="text-dusty-black-300 divide-x divide-white font-bold">
                <th class="sticky top-0 px-4 py-3 {{this.themeClasses.headerBg}}">
                  {{t "manage.manageClasses.students.name"}}
                </th>
                <th class="sticky top-0 w-1/2 px-4 py-3 {{this.themeClasses.headerBg}}">{{t "schoolClass.label"}}</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.studentRows as |student index|}}
                <tr data-test-row={{index}} class={{this.themeClasses.rowBg}}>
                  <td class="px-4 py-3">{{student.fullName}}</td>
                  <td class="px-4 py-3">
                    {{#each student.schoolClassesData as |schoolClassesData|}}
                      {{#if schoolClassesData.classNameAndTeachers}}
                        <div>
                          <span class="font-bold">
                            {{schoolClassesData.label}}
                          </span>
                          <span>
                            {{schoolClassesData.classNameAndTeachers}}
                          </span>
                        </div>
                      {{/if}}
                    {{/each}}
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
        <div class="border-dusty-black-50 border-t p-2 text-right">
          <UiButton data-test-cancel type="reset" {{on "click" (optional @closeModalAction)}}>
            {{t "manage.manageClasses.students.bulkActions.cancel"}}
          </UiButton>
          <UiButton
            type="submit"
            @appearance={{if this.isDeleting ButtonAppearance.muted ButtonAppearance.red}}
            @disabled={{this.isDeleting}}
          >
            <UiButtonState @isLoading={{this.isDeleting}}>
              {{t "manage.manageClasses.students.bulkActions.deleteStudents.delete" count=this.deletableStudentCount}}
            </UiButtonState>
          </UiButton>
        </div>
      </form>
    </div>
  </template>
}

export default FormDeleteStudents
